/**
 * Per-browser persistence for the five external connector handles.
 *
 * The server is the source of truth for tokens (Constitution C-5).
 * What we keep client-side is just the `Connected.user_handle` —
 * a 16-char SHA-256 prefix of the underlying email / Stripe account
 * id. Lets the UI label cards "Connected as ab12cd34…" across reloads
 * without refetching from the API.
 */
import type { ConnectionHandle, ConnectorSlug } from '@/api/external-connectors';

const KEY_PREFIX = 'gi-engine.integrations.handle';

function key(slug: ConnectorSlug): string {
  return `${KEY_PREFIX}.${slug}`;
}

export function loadHandle(slug: ConnectorSlug): ConnectionHandle | null {
  try {
    const raw = localStorage.getItem(key(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConnectionHandle;
    return parsed;
  } catch {
    return null;
  }
}

export function saveHandle(slug: ConnectorSlug, handle: ConnectionHandle): void {
  try {
    localStorage.setItem(key(slug), JSON.stringify(handle));
  } catch {
    /* quota exceeded → silently ignore; the API is still source of truth */
  }
}

export function clearHandle(slug: ConnectorSlug): void {
  try {
    localStorage.removeItem(key(slug));
  } catch {
    /* ignore */
  }
}

/** Stash the slug an OAuth flow was initiated for so the SPA's
 *  callback can recover it after Google redirects back. */
const PENDING_KEY = 'gi-engine.integrations.oauth-pending';

export function setOAuthPending(slug: ConnectorSlug, state: string): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ slug, state }));
  } catch {
    /* ignore */
  }
}

export function consumeOAuthPending(): { slug: ConnectorSlug; state: string } | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw) as { slug: ConnectorSlug; state: string };
  } catch {
    return null;
  }
}
